import { useState, useEffect, useCallback } from 'react';

export const usePronunciation = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [voices, setVoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load voices when component mounts or voices change
  const loadVoices = useCallback(() => {
    if ('speechSynthesis' in window) {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      setIsSupported(true);
      setIsLoading(false);
      
      console.log('Voices loaded:', availableVoices.length);
      console.log('Available voices:', availableVoices.map(v => `${v.name} (${v.lang})`));
    } else {
      setIsSupported(false);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load voices immediately
    loadVoices();

    // Some browsers need to wait for voices to be loaded
    if ('speechSynthesis' in window) {
      // Set up voice loading listener
      const handleVoicesChanged = () => {
        loadVoices();
      };

      window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);

      // Fallback: try loading voices after a delay
      const timer = setTimeout(() => {
        loadVoices();
      }, 1000);

      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
        clearTimeout(timer);
      };
    }
  }, [loadVoices]);

  const speak = useCallback((text, options = {}) => {
    if (!isSupported || !text) {
      console.warn('Speech synthesis not supported or no text provided');
      return Promise.reject(new Error('Speech synthesis not available'));
    }

    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      // Find the best voice
      const findBestVoice = () => {
        const currentVoices = window.speechSynthesis.getVoices();
        
        // Try to find Arabic voice first
        let selectedVoice = currentVoices.find(voice => 
          voice.lang.includes('ar') || voice.name.toLowerCase().includes('arabic')
        );

        // If no Arabic voice, try French (closer to Algerian pronunciation)
        if (!selectedVoice) {
          selectedVoice = currentVoices.find(voice => 
            voice.lang.includes('fr') || voice.name.toLowerCase().includes('french')
          );
        }

        // Fallback to English or default
        if (!selectedVoice) {
          selectedVoice = currentVoices.find(voice => 
            voice.lang.includes('en') || voice.default
          );
        }

        // Last resort: use first available voice
        if (!selectedVoice && currentVoices.length > 0) {
          selectedVoice = currentVoices[0];
        }

        return selectedVoice;
      };

      const selectedVoice = findBestVoice();
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        console.log('Using voice:', selectedVoice.name, selectedVoice.lang);
      } else {
        console.warn('No voices available, using default');
      }

      // Configure utterance
      utterance.rate = options.rate || 0.8;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;

      // Set up event handlers
      utterance.onstart = () => {
        console.log('Speech started:', text);
        resolve(true);
      };

      utterance.onend = () => {
        console.log('Speech ended');
      };

      utterance.onerror = (event) => {
        console.error('Speech error:', event.error);
        reject(new Error(`Speech synthesis failed: ${event.error}`));
      };

      // Speak the text
      try {
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('Failed to speak:', error);
        reject(error);
      }
    });
  }, [isSupported]);

  const stop = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  return {
    speak,
    stop,
    isSupported,
    voices,
    isLoading
  };
};

export default usePronunciation;